/**
 * activeView.js - Google ActiveView evidence for the slot lifecycle. Frontend twin of
 * proxy-server/lifecycle/activeView.js (the source of truth; pinned by activeViewParity.test.js).
 * This is the LOCAL FALLBACK used by AdLifecycleView.buildSlotLifecycles when the proxy's
 * lifecycle answer is unavailable. Change both files together.
 * Spec: docs/sdlc/viewability-activeview/20-requirements.md (DA1-DA15), reviewed in 25-adtech-review.md.
 *
 * Two evidence shapes, kept apart:
 *   - APP measurement ping  /pcs/activeview?xai&sig&id&acvw=<blob>   (GMA SDK). Signed, bound to the
 *     creative by `xai`, carries exposure buckets (`tos`/`mtos`, ms, inside the `acvw` blob) and NO
 *     verdict. `acvw.c=1` is a flag, never the GMA correlator. No iu, no `type`.
 *   - WEB ping  /pagead/gen_204?id=av-js  (GPT / webview). May carry a literal `type=viewable|
 *     noviewable|startEarly`, scalar `tos`/`mtos`, `dt`, `iu`, `c`. Only this shape can ever
 *     produce a verdict-moving issue (the slot-level issue is WP2; entry-level status is here).
 *
 * Join (one ping -> at most one slot; ambiguous attaches to none; a ping BEFORE the GAM request
 * never attaches in any tier - DA2):
 *   0. 'xai'          ping xai is in exactly one slot's GAM row validData.expanded.impXaiAll/impXai. proven.
 *   1. 'correlator'   ping correlator AND iu match a slot (refresh: the latest request at or before
 *                     the ping)                                          proven; INFERRED on a refreshed slot
 *                     ping correlator alone matches exactly one slot      inferred
 *   2. 'iu_window'    ping carries an iu, 0 <= ping - request < 30 s, no later same-iu request in
 *                     between. Never time-only (a ping with no iu cannot reach this tier).  inferred
 *
 * Logcat: a logcat ping never attaches and a logcat GAM slot always gets `activeView: []` (DA11/Q5).
 * Pure, never throws, JSON-safe (no Sets, no undefined).
 */

export const WINDOW_MS = 30000;
export const NOT_VIEWABLE_CODE = 'activeview_not_viewable';
export const NOT_VIEWABLE_MSG = 'Google ActiveView reports not viewable (ping type=noviewable)';

/* ---------- classify (DA3) ---------- */

function pathOf(url) {
  const i = url.indexOf('?');
  return (i < 0 ? url : url.slice(0, i)).toLowerCase();
}
const isAppUrl = (url) => pathOf(url).includes('/pcs/activeview');
const isWebUrl = (url) => pathOf(url).includes('/pagead/gen_204') && /[?&]id=av-js(?:[&#]|$)/i.test(url);

/** An ActiveView ping by saved type OR by URL (saved HARs keep the stale google_impression type). */
export function isActiveViewRow(row) {
  try {
    if (!row || typeof row !== 'object') return false;
    if (String(row.requestType || '').toLowerCase() === 'activeview_measurement') return true;
    if (String(row.type || '').toLowerCase() === 'activeview_measurement') return true;
    const url = typeof row.url === 'string' ? row.url : '';
    return !!url && (isAppUrl(url) || isWebUrl(url));
  } catch {
    return false;
  }
}

/* ---------- parse (DA9) ---------- */

function safeDecode(s) {
  try { return decodeURIComponent(s); } catch { return s; }
}

function parsePairs(str) {
  const out = Object.create(null);
  if (typeof str !== 'string' || !str) return out;
  for (const pair of str.split('&')) {
    if (!pair) continue;
    const eq = pair.indexOf('=');
    const k = safeDecode(eq < 0 ? pair : pair.slice(0, eq));
    if (k in out) continue;
    out[k] = eq < 0 ? '' : safeDecode(pair.slice(eq + 1));
  }
  return out;
}

function queryOf(url) {
  const noHash = url.split('#')[0];
  const i = noHash.indexOf('?');
  return i < 0 ? '' : noHash.slice(i + 1);
}

/** '0,0,1500' -> [0,0,1500]; '2500' -> [2500]; '', 'abc', '1,,2' -> null. Values are ms. */
function parseBuckets(v) {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s) return null;
  const nums = [];
  for (const part of s.split(',')) {
    const t = part.trim();
    if (t === '') return null;
    const n = Number(t);
    if (!Number.isFinite(n)) return null;
    nums.push(n);
  }
  return nums;
}

function numOrNull(v) {
  if (v == null || String(v).trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

const strOrNull = (v) => (typeof v === 'string' && v !== '' ? v : null);

function toMs(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (!v) return 0;
  const n = Date.parse(v);
  return Number.isFinite(n) ? n : 0;
}

/** Same field order as buildSlotLifecycles' ts() so a ping and its GAM request share one clock. */
function rowTs(row) {
  return toMs(row.startedAt) || toMs(row.startedAtMs) || toMs(row.timing && row.timing.start)
    || toMs(row.startedAtISO) || toMs(row.timestamp) || toMs(row.ts) || toMs(row.time) || 0;
}

/**
 * Parse one ActiveView ping row. Returns null for a non-ActiveView row, a non-object, or a ping with
 * neither `_k` nor `id` (DA1). `type` is kept as sent (`typeLc` is the comparison form). Web shape
 * only reads `type`. `correlator` is the top-level `correlator`/`c`, NEVER `acvw.c`.
 */
export function parseActiveViewPing(row) {
  try {
    if (!isActiveViewRow(row)) return null;
    const key = row._k != null ? row._k : row.id;
    if (key == null) return null;
    const url = typeof row.url === 'string' ? row.url : '';
    const p = parsePairs(queryOf(url));
    const form = isAppUrl(url) ? 'app' : 'web';

    let blob = typeof p.acvw === 'string' ? p.acvw : '';
    if (blob && !blob.includes('=') && /%3d/i.test(blob)) blob = safeDecode(blob);
    const b = parsePairs(blob);

    const tosRaw = b.tos != null ? b.tos : p.tos;
    const mtosRaw = b.mtos != null ? b.mtos : p.mtos;
    const tosBuckets = parseBuckets(tosRaw);
    const mtosBuckets = parseBuckets(mtosRaw);
    const type = form === 'web' ? strOrNull(p.type) : null;

    return {
      key,
      form,
      vendor: 'Google',
      type,
      typeLc: type ? type.toLowerCase() : null,
      id: strOrNull(p.id),
      sig: strOrNull(p.sig),
      tos: tosBuckets && tosBuckets.length === 1 ? tosBuckets[0] : null,
      mtos: mtosBuckets && mtosBuckets.length === 1 ? mtosBuckets[0] : null,
      tosBuckets,
      mtosBuckets,
      dt: numOrNull(p.dt),
      xai: strOrNull(p.xai),
      adk: strOrNull(p.adk),
      iu: strOrNull(p.iu),
      correlator: strOrNull(p.correlator) || strOrNull(p.c),
      ts: rowTs(row),
      logcat: row.captureSource === 'logcat',
    };
  } catch {
    return null;
  }
}

function collectPings(rows) {
  const out = [];
  if (!Array.isArray(rows)) return out;
  rows.forEach((row, order) => {
    const ping = parseActiveViewPing(row);
    if (ping) out.push({ ping, order });
  });
  return out;
}

/* ---------- join (DA2, DA4) ---------- */

function infoOf(lc) {
  const none = { usable: false, unit: '', ts: 0, corr: '', xais: new Set(), refreshed: false };
  if (!lc || typeof lc !== 'object') return none;
  const gamRow = lc.phases && lc.phases.gam && lc.phases.gam.row;
  const logcat = !!(gamRow && gamRow.captureSource === 'logcat');
  const vd = gamRow && gamRow.validData;
  const ex = (vd && vd.expanded) || {};
  const xais = new Set();
  if (typeof ex.impXai === 'string' && ex.impXai) xais.add(ex.impXai);
  if (Array.isArray(ex.impXaiAll)) for (const x of ex.impXaiAll) if (typeof x === 'string' && x) xais.add(x);
  return {
    usable: !logcat,
    unit: String(lc.adUnit || ''),
    ts: Number(lc.timestamp) || 0,
    corr: lc.correlator ? String(lc.correlator) : '',
    xais,
    refreshed: false,
  };
}

function buildInfos(lcs) {
  const infos = lcs.map(infoOf);
  // Refreshed = another request for the same ad unit within the join window (AC 2.62 rule).
  infos.forEach((n, i) => {
    if (!n.unit) return;
    n.refreshed = infos.some((o, j) => j !== i && o.unit === n.unit && Math.abs(o.ts - n.ts) < WINDOW_MS);
  });
  return infos;
}

function joinPing(ping, infos) {
  const none = { index: -1, joinedBy: null, tier: null };
  const ambiguous = { index: -1, joinedBy: 'ambiguous', tier: null };
  if (ping.logcat) return none;
  const t = ping.ts;

  // Is there a later request for the same ad unit that fired at or before the ping?
  const laterSameSlotGam = (i) => {
    const unit = infos[i].unit;
    if (!unit) return false;
    const base = infos[i].ts;
    return infos.some((o, j) => j !== i && o.unit === unit && o.ts > base && o.ts <= t);
  };

  // Tier 0: xai (exact, per creative).
  if (ping.xai) {
    const owners = [];
    infos.forEach((n, i) => { if (n.usable && n.xais.has(ping.xai)) owners.push(i); });
    if (owners.length > 1) return ambiguous;
    if (owners.length === 1) {
      return t >= infos[owners[0]].ts ? { index: owners[0], joinedBy: 'xai', tier: 'proven' } : none;
    }
  }

  const corr = ping.correlator;
  const iu = ping.iu;

  // Tier 1: correlator (+ iu agreement).
  if (corr) {
    const sameCorr = [];
    infos.forEach((n, i) => { if (n.usable && n.corr === corr) sameCorr.push(i); });
    if (iu) {
      const sameIu = sameCorr.filter((i) => infos[i].unit === iu);
      if (sameIu.length === 1) {
        const i = sameIu[0];
        if (!(infos[i].ts <= t)) return none;
        return { index: i, joinedBy: 'correlator', tier: infos[i].refreshed ? 'inferred' : 'proven' };
      }
      if (sameIu.length > 1) {
        const before = sameIu.filter((i) => infos[i].ts <= t);
        const latest = before.filter((i) => !laterSameSlotGam(i));
        if (latest.length === 1) {
          return { index: latest[0], joinedBy: 'correlator', tier: infos[latest[0]].refreshed ? 'inferred' : 'proven' };
        }
        return ambiguous;
      }
      // correlator matched a slot but no iu agrees: not a correlator join; try the window.
    } else if (sameCorr.length === 1) {
      const i = sameCorr[0];
      return infos[i].ts <= t ? { index: i, joinedBy: 'correlator', tier: 'inferred' } : none;
    } else if (sameCorr.length > 1) {
      return ambiguous;
    }
  }

  // Tier 2: iu + 30 s window after the request. Requires an iu on the ping.
  if (iu) {
    const cands = [];
    infos.forEach((n, i) => {
      if (!n.usable || n.unit !== iu) return;
      if (corr && n.corr && n.corr !== corr) return;
      const diff = t - n.ts;
      if (!(diff >= 0 && diff < WINDOW_MS)) return;
      if (laterSameSlotGam(i)) return;
      cands.push(i);
    });
    if (cands.length === 1) return { index: cands[0], joinedBy: 'iu_window', tier: 'inferred' };
    if (cands.length > 1) return ambiguous;
  }
  return none;
}

/* ---------- entries ---------- */

function issueFor(ping) {
  return {
    severity: 'warn',
    message: NOT_VIEWABLE_MSG,
    level: 'warn',
    msg: NOT_VIEWABLE_MSG,
    phase: 'viewable',
    code: NOT_VIEWABLE_CODE,
    rowKey: ping.key,
  };
}

/**
 * Entries for ONE slot, ordered by ping time. Entry status (DA5, entry level only):
 *   warn  the deciding ping: explicit type=noviewable, tier proven, latest ping for its xai (pings
 *         with no xai are grouped per slot); if several qualify the latest one decides
 *   info  any other explicit noviewable (inferred join, superseded, or not the decider)
 *   pass  everything else (viewable, startEarly, other type, app ping)
 */
function entriesForSlot(items) {
  const sorted = items.slice().sort((a, b) => (a.ping.ts - b.ping.ts) || (a.order - b.order));
  const groupOf = (it) => it.ping.xai || '';
  const isLatest = (it) => !sorted.some((o) => o !== it && groupOf(o) === groupOf(it)
    && ((o.ping.ts - it.ping.ts) || (o.order - it.order)) > 0);
  let decider = null;
  for (const it of sorted) {
    if (it.ping.typeLc === 'noviewable' && it.tier === 'proven' && isLatest(it)) decider = it;
  }
  return sorted.map((it) => {
    const ping = it.ping;
    const warn = it === decider;
    return {
      key: ping.key,
      vendor: 'Google',
      type: ping.type,
      id: ping.id,
      sig: ping.sig,
      tos: ping.tos,
      mtos: ping.mtos,
      tosBuckets: ping.tosBuckets ? ping.tosBuckets.slice() : null,
      mtosBuckets: ping.mtosBuckets ? ping.mtosBuckets.slice() : null,
      dt: ping.dt,
      xai: ping.xai,
      adk: ping.adk,
      iu: ping.iu,
      correlator: ping.correlator,
      joinedBy: it.joinedBy,
      tier: it.tier,
      rowKeys: [ping.key],
      status: warn ? 'warn' : (ping.typeLc === 'noviewable' ? 'info' : 'pass'),
      issues: warn ? [issueFor(ping)] : [],
    };
  });
}

/**
 * WP2 (DA5/E3): the ONE verdict-moving ActiveView finding. The deciding entry (explicit type=noviewable,
 * proven join, latest ping for its xai / slot) has status 'warn'; push exactly one slot-level
 * `activeview_not_viewable` issue and recompute overallStatus with the assembler's own rule. Logcat
 * slots never carry entries, so they never get here. Idempotent (a second call adds nothing).
 */
function applyVerdict(lc) {
  try {
    const decider = (lc.activeView || []).find((e) => e && e.status === 'warn' && Array.isArray(e.issues) && e.issues.length);
    if (!decider) return;
    if (!Array.isArray(lc.issues)) lc.issues = [];
    if (lc.issues.some((i) => i && i.code === NOT_VIEWABLE_CODE)) return;
    lc.issues.push({
      level: 'warn',
      msg: NOT_VIEWABLE_MSG,
      phase: 'viewable',
      code: NOT_VIEWABLE_CODE,
      rowKey: decider.key,
    });
    lc.overallStatus = lc.issues.some((i) => i && i.level === 'fail') ? 'fail'
      : lc.issues.some((i) => i && i.level === 'warn') ? 'warn'
      : 'pass';
  } catch { /* leave the lifecycle as the assembler built it */ }
}

/**
 * Give every lifecycle an `activeView` array (possibly empty) and push each joinable ping onto the
 * slot it measured. Mutates and returns `lifecycles`. Pushes the slot-level
 * `activeview_not_viewable` issue and recomputes overallStatus for the deciding ping only (WP2).
 */
export function attachActiveView(lifecycles, rows) {
  try {
    const list = Array.isArray(lifecycles) ? lifecycles : [];
    for (const lc of list) if (lc && typeof lc === 'object') lc.activeView = [];
    if (!list.length) return list;
    const pings = collectPings(rows);
    if (!pings.length) return list;
    const infos = buildInfos(list);
    const bySlot = new Map();
    for (const { ping, order } of pings) {
      const j = joinPing(ping, infos);
      if (j.index < 0 || !j.joinedBy || j.joinedBy === 'ambiguous') continue;
      if (!bySlot.has(j.index)) bySlot.set(j.index, []);
      bySlot.get(j.index).push({ ping, order, joinedBy: j.joinedBy, tier: j.tier });
    }
    for (const [index, items] of bySlot) {
      const lc = list[index];
      if (lc && typeof lc === 'object') {
        lc.activeView = entriesForSlot(items);
        applyVerdict(lc);
      }
    }
    return list;
  } catch {
    return lifecycles;
  }
}

/**
 * Slim rollup for summarizeLifecycles: counts classified pings whether attached or not.
 * `other` includes pings with no `type` (every app ping).
 */
export function activeViewRollup(rows, lifecycles) {
  const out = { pingCount: 0, attached: 0, unattached: 0, viewable: 0, noviewable: 0, other: 0 };
  try {
    const pings = collectPings(rows);
    out.pingCount = pings.length;
    for (const { ping } of pings) {
      if (ping.typeLc === 'viewable') out.viewable += 1;
      else if (ping.typeLc === 'noviewable') out.noviewable += 1;
      else out.other += 1;
    }
    for (const lc of (Array.isArray(lifecycles) ? lifecycles : [])) {
      if (lc && Array.isArray(lc.activeView)) out.attached += lc.activeView.length;
    }
    out.unattached = Math.max(0, out.pingCount - out.attached);
  } catch { /* zeroed rollup */ }
  return out;
}
